import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  ViewOptionsCheckbox,
} from 'components/ViewOptions';
import Accordion from 'components/Accordion';
import './styles.scss';

import { EVENT_TYPES, EVENT_COLORS } from 'utils/constants';

const VisibleItem = ({ name, label, onChange, options, checkColor = '#676767' }) => {
  const option = options?.[name] ?? false;

  const inputProps = {
    onChange,
    checked: option,
    className: 'calendar-sidepanel-checkbox',
    backgroundColor: '#f6f6f6',
  };

  return (
    <ViewOptionsCheckbox
      id={`${name}`}
      inputProps={inputProps}
      clickwrapColor={checkColor}
    >
      {label}
    </ViewOptionsCheckbox>
  );
};

const CalendarSidePanel = ({
  options,
  handleEventTypeToggle,
  handleExternalCalendarToggle,
  handleManageCalendar,
  toggleSidePanel,
}) => {
  const userInfo = useSelector((state) => state.global.userInfo);
  const { integrationsEnabled, integrations = [] } = userInfo || {};

  // #TODO: decouple the component calendar from the calendar page.
  // To prevent error in storybook.
  // console.log('user info', userInfo);
  if (!userInfo) {
    return null;
  }

  const validIntegrations = integrations.filter(i => i.grantStatus === 'valid');

  // Filter out external calendars that were created from AP.
  const filteredExternalCalendars = useMemo(() => {
    return validIntegrations.map(validIntegration => {
      // If the calendar has a calendarType, that means it was created from AP.
      const filteredCalendars = validIntegration.loginCalendars.filter(lc => !lc.calendarType);
      return {
        ...validIntegration,
        loginCalendars: filteredCalendars,
      };
    });
  }, [validIntegrations]);

  // Check if after filtering calendars, any more exist.
  const hasExternalCalendarsSynced = filteredExternalCalendars.some(
    integration => Array.isArray(integration.loginCalendars) && integration.loginCalendars.length > 0,
  );

  const hasEnabledIntegrations = integrationsEnabled && validIntegrations.length > 0;

  // We don't want to show an empty dropdown, but because we have to filter from the app instead of the
  // query, we need to fun these checkers.
  const showExternalCalendars = hasExternalCalendarsSynced && hasEnabledIntegrations;

  return (
    <>
      <div className="calendar-sidepanel__header">
        <button
          onClick={toggleSidePanel}
          className="calendar-sidepanel__toggle-button"
        />
        {hasEnabledIntegrations && (
          <button
            onClick={handleManageCalendar}
            className="calendar-sidepanel__manage-button"
          >
            Manage calendars
          </button>
        )}
      </div>

      <div className="calendar-sidepanel__body">
        {/*
          These accordions are disabled as accordions for now with a note in
          APP-8089 that says they are temporarily uncollapsible. If we don't
          make them uncollapsible in the future, they should just be regular
          elements. Pointer events are disabled in the css.
        */}
        <Accordion title="Aisle Planner"
          decorator={false}
          open
        >
          {EVENT_TYPES.map((item) => (
            <VisibleItem
              key={item.value}
              name={item.value}
              label={item.label}
              checkColor={item.color}
              options={options}
              onChange={handleEventTypeToggle}
            />
          ))}
        </Accordion>
        {showExternalCalendars &&
          (filteredExternalCalendars.map((integration) => (
            <Accordion key={integration.id}
              title={integration.provider}
              decorator={false}
              open
            >
              {integration.loginCalendars
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((calendar) => {
                  // By default, microsoft doesn't provide calendar color unless user explicitly sets it.
                  // Provide a default blue pulled from outlook if the hex color isn't provided.
                  const hexColor = !calendar.hexColor && integration.provider === 'microsoft' ?
                    '#0F6CBD' : calendar.hexColor || EVENT_COLORS.default;
                  return (
                    <VisibleItem
                      key={calendar.id}
                      name={calendar.id}
                      label={calendar.name}
                      checkColor={hexColor}
                      options={options?.externalCalendarsDisplay || {}}
                      onChange={handleExternalCalendarToggle}
                    />
                  );
                })}
            </Accordion>
          )))}
      </div>
    </>
  );
};

export default CalendarSidePanel;
